
import {
  ConfigResetListDto,
  ConfigResetRepositoryInterface,
  RestoreDatabase,
  RestoreTaskId,
  RestoreTaskRepositoryInterface,
  RestoreTaskSummaryDto,
  RestoreWebsite,
  TaskProgressStatus,
} from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import RestoreTaskDetailsDialog from "@/components/dialogs/backups/RestoreTaskDetailsDialog.vue";
import WebsiteRepositoryInterface, { Website } from "@/services/Website/interfaces";
import { ServerInterface, ServerRepositoryInterface, ServerType } from "@/services/Server/Interfaces";

@Component({
  mixins: [Themeable],
  inject: ['restoreTaskRepository', 'configResetRepository', 'websiteRepository', 'serverRepository'],
  components: {
    RestoreTaskDetailsDialog,
  },
})
export default class AllRestoreTasks extends Vue {
  public loading: boolean = false;
  public currentPage: number = 1;
  public itemsPerPage: number = 10;
  public totalItems: number = 0;

  public restoreTaskRepository: RestoreTaskRepositoryInterface;
  public configResetRepository: ConfigResetRepositoryInterface;
  public websiteRepository: WebsiteRepositoryInterface;
  public serverRepository: ServerRepositoryInterface;

  public restores: RestoreTaskSummaryDto[] = [];
  public restoredWebsitesMap: {[index: number]: Website} = {};
  public restoreTask: RestoreTaskSummaryDto;
  public detailsDialog: boolean = false;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Backup ID', value: 'backupId' },
    { text: 'Origin Server', value: 'backup.targetOriginServer.originServerId' },
    { text: 'Main Website', value: 'websiteRestores.mainWebsiteId' },
    { text: 'Status', value: 'status' },
    { text: 'Runtime', value: 'startDate' },
    { text: 'End At', value: 'endDate' },
    { text: 'Actions', value: 'actions', sortable: false },
  ];

  public detailsDialogId: number | null = null;
  public websiteRestoreData: RestoreWebsite | null = null;
  public databaseRestoreData: RestoreDatabase | null = null;
  public configRestoreData: ConfigResetListDto | null = null;

  private statusLabels = {
    0: 'Pending',
    1: 'Running',
    2: 'Finished',
    3: 'Failed',
  };

  private typeLabels = {
    0: 'config/server',
    1: 'server',
    2: 'config',
  };

  private _searchServerId: string = '';
  private servers: ServerInterface[] = [];

  private _searchWebsiteId: number = 0;
  private _websiteSearch: string = '';
  private websiteSearchLoading: boolean = true;
  private websites: Website[] = [];

  get isWebsiteBackup(): boolean {
    return this.restoreTask?.backup?.type === 'file';
  }

  get isDatabaseBackup(): boolean {
    return this.restoreTask?.backup?.type === 'mysql';
  }

  get searchServerId(): string {
    return this._searchServerId;
  }

  set searchServerId(value: string) {
    this._searchServerId = value;
    this.currentPage = 1;
    this._searchWebsiteId = 0;

    this.search();
  }

  public get websiteSearch(): string {
    return this._websiteSearch;
  }

  public set websiteSearch(val: string) {
    if (val && val.length < 3) {
      return;
    }

    this._websiteSearch = `domain=${val}`;
    this.websiteSearchLoading = true;

    this.websiteRepository.searchWebsites(undefined, val).then((websites) => {
      this.websites = websites.Websites;
      this.websiteSearchLoading = false;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });
  }

  get searchWebsiteId(): number {
    return this._searchWebsiteId;
  }

  set searchWebsiteId(value: number) {
    this._searchWebsiteId = value;
    this.currentPage = 1;
    this._searchServerId = '';

    this.search();
  }

  public getMainWebsite(restores: { websiteId: number, websiteDomain: string, isMain: boolean}[]) {
    return restores.find(x => x.isMain);
  }

  @Watch('detailsDialog')
  public onDetailsDialogChange(newVal: boolean): void {
    if (!newVal) {
      // Dialog was closed
      this.restoreTask = null;
      this.websiteRestoreData = null;
      this.databaseRestoreData = null;
      this.configRestoreData = null;
    }
  }

  public async showDetailsDialogFor(restoreId: RestoreTaskId): Promise<void> {
    if (this.restoreTask && this.restoreTask.id === restoreId && this.restoreTask.status > TaskProgressStatus.Running) return;

    this.restoreTask = await this.restoreTaskRepository.getRestoreTask(restoreId);
    await this.fetchRestoreTask(this.restoreTask.id, Object.keys(JSON.parse(this.restoreTask.websiteRestores[0].restoreConfigsJson)).length > 0);

    if (!this.restoreTask ) {
      return;
    }
    this.detailsDialog = true;
    // this.detailsDialog = 'restore-task-details-dialog';
  }


  public statusLabel(status: number): string {
    return this.statusLabels[status] || 'Unknown';
  }

  public typeLabel(type: number): string {
    return this.typeLabels[type] || 'Unknown';
  }

  public async initialize(): Promise<void> {
    this.restoreTask = null;

    await  this.serverRepository.getServersOfType(ServerType.ORIGIN).then((servers) => {
      this.servers = servers;
    }).catch((err) => {
      this.$errorDialog.show(err.message);
    });

    await this.search();
  }

  public async created(): Promise<void> {
    await this.initialize();
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getRestoreTasks();
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async getRestoreTasks(): Promise<void> {
    const result = await this.restoreTaskRepository.searchRestoreTasks(
      (this._searchServerId ?? '') !== '' ? this._searchServerId : undefined,
      (this._searchWebsiteId ?? 0) !== 0 ? this._searchWebsiteId : undefined,
      {
      page: this.currentPage,
      pageSize: this.itemsPerPage,
    });

    //const websites = await this.websiteRepository.getWebsitesByIds(Array.from(new Set(result?.data.map(x => x.websiteRestores[0]['websiteId']) ?? [])));

    this.totalItems = result?.total ?? 0;
    this.restores = result?.data ?? [];
    /*this.restoredWebsitesMap = websites.reduce((group, website) => {
      group[website.id as number] = website;

      return group;
    }, {} as {[index: number]: Website});*/
  }

  public async changePage(): Promise<void> {
    await this.search();
  }

  private async fetchRestoreTask(restoreId: RestoreTaskId, hasConfigs: boolean): Promise<void> {
    try {
      await this.fetchAdditionalData(restoreId, hasConfigs);
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  private async fetchAdditionalData(restoreId: RestoreTaskId, hasConfigs: boolean): Promise<void> {
    if (this.restoreTask) {
      if (hasConfigs) {
        const configReset = await this.configResetRepository.getConfigResetByTask(restoreId);
        if (configReset) {
          this.configRestoreData = configReset;
        }
      }
    }
  }

  private async fetchWebsiteRestoreData(restoreId: RestoreTaskId): Promise<RestoreWebsite | null> {
    try {
      return await this.restoreTaskRepository.getWebsiteRestoreData(restoreId);
    } catch (err) {
      await this.onError(err);
      return null
    }
  }

  private async onError(err: unknown): Promise<void> {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
